import { emailBodyContainsShareUrl, type ShareEmailSendMode } from '@shared/share-email-delivery';

export interface ShareEmailDraftInput {
  shareType: 'preview' | 'final';
  projectId: string;
  projectName: string;
  /** Saved title from the share generation dialog. */
  shareTitle?: string | null;
  senderName: string;
  url: string;
  /** Share row id; forwarded to /api/emails/send so the email_out pulse links back to the share. */
  previewShareId?: number;
}

export interface ShareEmailDraft {
  key: string;
  subject: string;
  htmlContent: string;
  shareGreeting: true;
  previewShareId?: number;
  /** Exact intended URL; used to re-check the edited body at submission. */
  shareUrl?: string;
}

export function getShareEmailSendFields(
  draft: { previewShareId?: number; shareUrl?: string } | undefined,
  html: string,
  deliveryRequestId: string,
  shareSendMode: ShareEmailSendMode,
): { previewShareId?: string; shareSendMode?: ShareEmailSendMode; deliveryRequestId?: string } {
  if (!draft?.previewShareId) return { deliveryRequestId };
  if (!draft.shareUrl || !emailBodyContainsShareUrl(html, draft.shareUrl)) return { deliveryRequestId };
  return { previewShareId: String(draft.previewShareId), shareSendMode, deliveryRequestId };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

interface GreetingRecipient {
  email: string;
  name?: string | null;
  type: 'to' | 'cc' | 'bcc';
}

interface GreetingContext {
  suggestions: Array<{ email: string; name: string | null; firstName?: string | null; isInternal: boolean; source?: string; contactId?: string | null }>;
  internalEmails?: string[];
  internalDomains?: string[];
}

/** Only share drafts use generated greetings. Never reveal a BCC name in the body. */
export function getShareEmailGreeting(recipients: GreetingRecipient[], context: GreetingContext): string {
  const fallback = '<p>Hallo zusammen,</p>';
  // Older cached suggestion responses have no complete internal classification.
  if (!context.internalEmails || !context.internalDomains) return fallback;
  const normalize = (value: string) => value.trim().toLowerCase();
  const internalEmails = new Set(context.internalEmails.map(normalize));
  const internalDomains = new Set(context.internalDomains.map(normalize));
  const suggestions = new Map(context.suggestions.map(s => [normalize(s.email), s]));
  const external = new Map<string, GreetingRecipient>();
  for (const recipient of recipients) {
    const email = normalize(recipient.email);
    if (!email || internalEmails.has(email) || internalDomains.has(email.split('@')[1]) || suggestions.get(email)?.isInternal) continue;
    const prior = external.get(email);
    if (!prior || prior.type === 'bcc') external.set(email, recipient);
  }
  if (external.size !== 1) return fallback;
  const [email, recipient] = [...external.entries()][0];
  if (recipient.type === 'bcc') return fallback;
  const suggestion = suggestions.get(email);
  // A company mailbox label is not a person's first name.
  if (suggestion?.source === 'client_contact' && !suggestion.contactId && !suggestion.firstName) return fallback;
  // Prefer a saved first name; only use unambiguous display names as fallback.
  const displayName = (suggestion?.name || recipient.name || '').trim();
  const firstName = suggestion?.firstName?.trim()
    || (/[,<>@]/.test(displayName) ? '' : displayName.split(/\s+/)[0]);
  if (!firstName || /^(herr|frau|dr\.?|prof\.?|team|info|office)$/i.test(firstName)) return fallback;
  return `<p>Hallo ${escapeHtml(firstName)},</p>`;
}

/** Replace only our own untouched leading greeting, never the author's edits. */
export function updateShareEmailGreeting(html: string, previousGreeting: string, nextGreeting: string): string {
  return html.startsWith(previousGreeting)
    ? nextGreeting + html.slice(previousGreeting.length)
    : html;
}

export function buildShareEmailDraft(input: ShareEmailDraftInput): ShareEmailDraft {
  const isFinal = input.shareType === 'final';
  const label = isFinal ? 'Final' : 'Preview';
  const deliveryLabel = isFinal ? 'finale Auslieferung' : 'neue Preview';
  const rawSenderName = input.senderName.trim() || 'Acolori Team';
  const senderFirstName = escapeHtml(
    rawSenderName === 'Acolori Team'
      ? rawSenderName
      : rawSenderName.split(/\s+/)[0],
  );
  const projectName = escapeHtml(input.projectName.trim() || input.projectId);
  const url = escapeHtml(input.url);

  return {
    key: `${input.shareType}:${input.projectId}:${input.url}`,
    ...(input.previewShareId ? { previewShareId: input.previewShareId } : {}),
    shareUrl: input.url,
    subject: isFinal
      ? `${label} ist bereit`
      : `${input.projectName.replace(/_/g, ' ').replace(/\s+/g, ' ').trim() || input.projectId} - ${input.shareTitle?.trim() || 'Preview'}`,
    // The empty paragraph after the announcement is the intended place for an
    // optional personal note. Everything remains editable in Mail Out.
    htmlContent: [
      '<p>Hallo zusammen,</p>',
      `<p>${senderFirstName} hat für das Projekt <strong>${projectName}</strong> eine ${deliveryLabel}:</p>`,
      '<p><br></p>',
      `<p><a href="${url}">${url}</a></p>`,
      '<p><br></p>',
      '<p>Beste Grüße<br>Ben</p>',
    ].join(''),
    shareGreeting: true,
  };
}